import { Ellipse } from 'fabric';
import { applyHierarchicalSnap, ENDPOINT_SNAP_DISTANCE, snapValue } from './snapUtils';
import { GARDEN_OBJECT_TYPES, createGardenObject } from '../../../constants/gardenObjectTypes';
import { getObjectTypeById } from '../../../config';

/**
 * CircleTool - Handles circle/ellipse drawing logic
 *
 * Features:
 * - Draw ellipses by dragging from center
 * - Snap-to-grid support (center point) via snapUtils
 * - Snap-to-endpoint support (center snaps to other objects' snap points) via snapUtils
 * - Shift: Constrain to circle (1:1 aspect ratio)
 * - Ctrl: Temporarily disable grid snap (endpoint snap still active)
 * - Auto-switch to select mode after drawing
 */

/**
 * Calculate ellipse radii with modifiers
 *
 * @param {object} center - Center point {x, y}
 * @param {object} current - Current mouse point {x, y}
 * @param {boolean} shiftPressed - Constrain to circle (equal radii)
 * @param {boolean} ctrlPressed - Disable grid snap (endpoint snap still active)
 * @param {number} gridSize - Grid size in pixels
 * @param {boolean} snapEnabled - Whether snap is enabled
 * @param {fabric.Canvas} canvas - Canvas instance for endpoint snapping (optional)
 * @returns {object} Ellipse properties {centerX, centerY, radiusX, radiusY, snappedTo}
 */
export const calculateEllipseRadii = (
  center,
  current,
  shiftPressed,
  ctrlPressed,
  gridSize,
  snapEnabled,
  canvas = null
) => {
  // Apply hierarchical snap to center point (endpoint → grid → raw)
  const centerSnap = applyHierarchicalSnap(
    center,
    canvas,
    gridSize,
    snapEnabled,
    ctrlPressed,
    ENDPOINT_SNAP_DISTANCE
  );

  let { x: centerX, y: centerY } = centerSnap.point;

  // Calculate radii (distance from center to current point)
  let radiusX = Math.abs(current.x - centerX);
  let radiusY = Math.abs(current.y - centerY);

  // Snap radii to grid (not to endpoints, just grid)
  radiusX = snapValue(radiusX, gridSize, snapEnabled, ctrlPressed);
  radiusY = snapValue(radiusY, gridSize, snapEnabled, ctrlPressed);

  // Shift: Constrain to circle (equal radii)
  if (shiftPressed) {
    const radius = Math.max(radiusX, radiusY);
    radiusX = radius;
    radiusY = radius;
  }

  // Return radii with snap information
  return {
    centerX,
    centerY,
    radiusX,
    radiusY,
    snappedTo: centerSnap.snappedTo, // 'endpoint', 'grid', or 'none'
    snapTarget: centerSnap.target, // Fabric.js object if endpoint snap
  };
};

/**
 * Create a new ellipse with default styling
 *
 * @param {object} radii - Ellipse properties {centerX, centerY, radiusX, radiusY}
 * @param {boolean} isActive - Whether ellipse is selected/active
 * @param {string|null} objectTypeId - Garden object type ID (e.g., 'blumenampel', 'topf', 'teich')
 * @returns {Ellipse} Fabric.js Ellipse object with garden object metadata
 */
export const createEllipse = (radii, isActive = true, objectTypeId = null) => {
  const { centerX, centerY, radiusX, radiusY } = radii;

  // Get default style from config if objectTypeId provided
  let fillColor, strokeColor, strokeWidth = 2, opacity = 1;
  let objectConfig = null;

  if (objectTypeId) {
    objectConfig = getObjectTypeById(objectTypeId);
    if (objectConfig && objectConfig.defaultStyle) {
      const style = objectConfig.defaultStyle;
      fillColor = style.fill;
      strokeColor = style.stroke;
      strokeWidth = style.strokeWidth || 2;
      opacity = style.opacity !== undefined ? style.opacity : 1;
    }
  }

  // Fallback to neutral colors if no config found (for Zeichnen tab usage)
  if (!fillColor) {
    fillColor = 'rgba(107, 114, 128, 0.2)'; // Gray-500 with 20% opacity (neutral)
  }

  if (!strokeColor) {
    strokeColor = '#6B7280'; // Gray-500 (neutral)
  }

  const ellipse = new Ellipse({
    left: centerX,
    top: centerY,
    rx: radiusX, // Horizontal radius
    ry: radiusY, // Vertical radius
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    opacity: opacity,
    strokeUniform: true, // Maintain stroke width during scaling
    originX: 'center', // IMPORTANT: Draw from center
    originY: 'center',
    lockUniScaling: false, // Allow non-uniform scaling
    uniformScaling: false, // Disable uniform scaling constraint
    noScaleCache: false,
    selectable: false, // Not selectable while drawing
    evented: false, // No events while drawing
    hasControls: false, // No control handles while drawing
    hasBorders: false, // No border box while drawing
    hoverCursor: 'default', // No special cursor
    moveCursor: 'default', // No move cursor
    objectCaching: false, // Disable caching for live rendering
    perPixelTargetFind: false, // Bounding-box detection (ellipses have clean AABB)
  });

  // Apply garden object type system
  createGardenObject(GARDEN_OBJECT_TYPES.CIRCLE, ellipse, {
    // Keep legacy data for backward compatibility
    data: {
      shapeType: 'ellipse', // Renamed from 'type' to avoid conflict with garden object properties
      isActive,
      objectTypeId: objectTypeId, // Store the garden object type ID for config lookup
      gardenObjectType: objectTypeId, // Add both for compatibility
      createdAt: new Date().toISOString(),
      // Store original colors for selection/deselection and tab switching
      originalFill: fillColor,
      originalStroke: strokeColor,
      originalStrokeWidth: strokeWidth,
      originalOpacity: opacity,
      // Add config metadata if available
      ...(objectConfig && {
        isPlantable: objectConfig.isPlantable,
        castsShadow: objectConfig.castsShadow,
      }),
    },
  });

  // Set rotation cursor
  if (ellipse.controls && ellipse.controls.mtr) {
    ellipse.controls.mtr.cursorStyleHandler = () => 'grab';
  }

  return ellipse;
};

/**
 * Update ellipse appearance based on selection state
 *
 * @param {Ellipse} ellipse - Fabric.js Ellipse object
 * @param {boolean} isActive - Whether ellipse is selected/active
 */
export const updateEllipseAppearance = (ellipse, isActive) => {
  if (!ellipse || !ellipse.data) {
    return;
  }

  if (isActive) {
    // Selected: Use emerald-500 green
    ellipse.set({
      fill: 'rgba(16, 185, 129, 0.2)',
      stroke: '#10B981',
    });
  } else {
    // Deselected: Restore original config colors from object type definition
    const objectTypeId = ellipse.data.objectTypeId || ellipse.data.gardenObjectType;

    if (objectTypeId) {
      // Get config for this object type
      const objectConfig = getObjectTypeById(objectTypeId);

      if (objectConfig && objectConfig.defaultStyle) {
        // Restore colors from config
        ellipse.set({
          fill: objectConfig.defaultStyle.fill || 'rgba(107, 114, 128, 0.2)',
          stroke: objectConfig.defaultStyle.stroke || '#6B7280',
          strokeWidth: objectConfig.defaultStyle.strokeWidth || 2,
          opacity: objectConfig.defaultStyle.opacity !== undefined ? objectConfig.defaultStyle.opacity : 1,
        });
      } else {
        // Fallback to neutral colors if no config found
        ellipse.set({
          fill: 'rgba(107, 114, 128, 0.2)',
          stroke: '#6B7280',
          strokeWidth: 2,
          opacity: 1,
        });
      }
    } else {
      // No object type (legacy or Zeichnen tab usage) - use neutral colors
      ellipse.set({
        fill: 'rgba(107, 114, 128, 0.2)',
        stroke: '#6B7280',
        strokeWidth: 2,
        opacity: 1,
      });
    }
  }

  // Update data
  if (ellipse.data) {
    ellipse.data.isActive = isActive;
  }
};

/**
 * Get ellipse dimensions in cm
 *
 * @param {Ellipse} ellipse - Fabric.js Ellipse object
 * @param {number} pixelsPerCm - Conversion factor (default: 1 pixel = 1 cm)
 * @returns {object} Dimensions {radiusXCm, radiusYCm, widthCm, heightCm, areaSqm}
 */
export const getEllipseDimensions = (ellipse, pixelsPerCm = 1) => {
  // Get actual radii (including scale)
  const radiusXCm = (ellipse.rx * ellipse.scaleX) / pixelsPerCm;
  const radiusYCm = (ellipse.ry * ellipse.scaleY) / pixelsPerCm;

  // Calculate width/height (diameter)
  const widthCm = radiusXCm * 2;
  const heightCm = radiusYCm * 2;

  // Calculate area (π × radiusX × radiusY)
  const areaCm2 = Math.PI * radiusXCm * radiusYCm;
  const areaSqm = areaCm2 / 10000; // Convert cm² to m²

  return {
    radiusXCm: Math.round(radiusXCm * 100) / 100, // Round to 2 decimals
    radiusYCm: Math.round(radiusYCm * 100) / 100,
    widthCm: Math.round(widthCm * 100) / 100,
    heightCm: Math.round(heightCm * 100) / 100,
    areaSqm: Math.round(areaSqm * 100) / 100,
  };
};

/**
 * Update ellipse dimensions (for properties panel editing)
 *
 * @param {Ellipse} ellipse - Fabric.js Ellipse object
 * @param {object} radii - New radii {radiusXCm, radiusYCm}
 * @param {number} pixelsPerCm - Conversion factor
 */
export const updateEllipseDimensions = (ellipse, radii, pixelsPerCm = 1) => {
  const { radiusXCm, radiusYCm } = radii;

  // Convert cm to pixels
  const newRadiusX = radiusXCm * pixelsPerCm;
  const newRadiusY = radiusYCm * pixelsPerCm;

  ellipse.set({
    rx: newRadiusX,
    ry: newRadiusY,
    scaleX: 1,
    scaleY: 1,
  });
};

/**
 * Update ellipse position (for properties panel editing)
 *
 * @param {Ellipse} ellipse - Fabric.js Ellipse object
 * @param {object} position - New position {x, y} in cm (CENTER point)
 * @param {number} pixelsPerCm - Conversion factor
 */
export const updateEllipsePosition = (ellipse, position, pixelsPerCm = 1) => {
  const { x, y } = position;

  ellipse.set({
    left: x * pixelsPerCm,
    top: y * pixelsPerCm,
  });
};

/**
 * Check if ellipse is a circle (equal radii)
 *
 * @param {Ellipse} ellipse - Fabric.js Ellipse object
 * @param {number} tolerance - Tolerance for comparison (default: 0.01)
 * @returns {boolean} True if ellipse is a circle
 */
export const isCircle = (ellipse, tolerance = 0.01) => {
  const radiusX = ellipse.rx * ellipse.scaleX;
  const radiusY = ellipse.ry * ellipse.scaleY;
  return Math.abs(radiusX - radiusY) < tolerance;
};
